export const frontmatter = {
  title: "VK-пайплайн: как фото превращаются в статистику",
  slug: "vk-pipeline",
  category: "Источники данных",
  reading_minutes: 6,
  updated: "2026-04-29",
  abstract: "Откуда берутся агрегаты «что сейчас растёт», как мы привязываем посты к районам и что с этим не так.",
};

import { Callout, KeyFacts, Stats, Steps, TLDR } from "../../components/mdx";

# VK-пайплайн: как фото превращаются в статистику

<TLDR>
За цифрой «Белые 38%» стоят **69 262** поста группы «Я и гриб» за 11 лет. Каждое фото прогоняется через локальный мультимодальный классификатор **qwen3.5-vl** с constrained JSON-схемой на 18 видов. Посты привязываются к районам ЛО через NER + gazetteer.
</TLDR>

<Stats stats={[
  { number: "69 262", label: "постов в корпусе", hint: "2014–2026" },
  { number: "18", label: "классов в схеме", hint: "грибы и ягоды" },
  { number: "qwen3.5-vl", label: "модель", hint: "локально, LM Studio" },
  { number: "v13", label: "версия промта", hint: "2026-04-24" },
]} />

<KeyFacts title="Корпус" facts={[
  { label: "Источник", value: <>Открытое VK-сообщество <a href="https://vk.com/grib_spb">«Я и гриб / grib_spb»</a></> },
  { label: "Период", value: "2014 — апрель 2026" },
  { label: "Хранится", value: "text, photo_urls (до 10), date_ts, likes, reposts, views, post_id" },
  { label: "Не читаем", value: "приватные стены, сообщения, скрытые посты" },
  { label: "Привязка к району", value: "Natasha NER + gazetteer (~21k OSM-топонимов ЛО)" },
]} />

## Четыре стадии

Каждая идемпотентна: можно перезапускать, маркируется флагом в `vk_post`, повторный запуск обрабатывает только недоделанное.

<Steps steps={[
  {
    title: "Fetch — сбор постов",
    body: <>Официальное VK API, пагинация по 100, backoff на 429. Дедуп по <code>(vk_group, post_id)</code> с UNIQUE-индексом.</>,
  },
  {
    title: "Dates — извлечение даты похода",
    body: <>Дата публикации ≠ дата сбора. Регулярки покрывают ~60% постов: «15 августа 2024», «октябрь 2023» (день случайным числом 10–25, чтобы не было пика на 15-м), «вчера» / «в субботу». Ещё ~15% — LLM-фолбэк (Claude Haiku). Остальное — <code>not_found</code>.</>,
  },
  {
    title: "Photos — классификация",
    body: <>До 6 фото с поста (равномерный сэмплинг). qwen3.5-vl локально через LM Studio с <code>response_format: json_schema</code> по схеме из 18 видов грибов и ягод. Промт версионируется — при смене <code>PHOTO_PROMPT_VERSION</code> стадия перезапускается.</>,
  },
  {
    title: "District attribution — привязка к району",
    body: <>Natasha NER находит LOC-spans, gazetteer матчит exact/alias/trgm. Покрытие ~8% с avg confidence 0.988. Regex-фолбэк («выборгск*», донор-топонимы Лемболово → Всеволожский) добавляет покрытие ~до 60%, но даёт больше шума — пишется в <code>place_match.detected_places</code>, фильтрует уже агрегатор.</>,
  },
]} />

## История промта

<KeyFacts facts={[
  { label: "v7", value: "baseline на Gemma 3 12B, 13 видов" },
  { label: "v9", value: "merged chanterelle (лисичка + трубчатая + вороночник), loosened pine_bolete, 6-photo sampling" },
  { label: "v10", value: "балансировка porcini ↔ pine_bolete по тону шляпки, tie-break для смешанных корзин" },
  { label: "v11", value: "spring_mushroom → триплет morel/verpa/gyromitra, anti-porcini guard для сморчковой шапочки" },
  { label: "v12", value: "разводка палитр Leccinum, устраняет систематический снос подосиновика в pine_bolete" },
  { label: "v13", value: "ужесточение birch_bolete (видимая чешуйчатая ножка обязательна), небольшой tilt в porcini для неоднозначных коричневых шляпок" },
]} />

## Чему доверять нельзя

<Callout type="warn" title="Тип обозревателя, а не тип гриба">
Каждый автор фотографирует то, что ему интересно. Рыжики недопредставлены — их любят снимать меньше белых и лисичек. grib_spb честно показывает распределение **в корзинах любителей**, а не **в лесах**.
</Callout>

<Callout type="warn" title="Throwback-эффект">
В апреле-мае половина лент — архивные фото с лета. Без распознанной foray_date такой пост уйдёт в апрель, хотя снят в августе. Покрытие foray_date неполное — в sparse-месяцах throwback может сдвинуть картину на 10–15%.
</Callout>

<Callout type="warn" title="Визуальный bias модели">
Qwen, как любая vision-модель, обучена на западных наборах — уверенно зовёт «белым» любой коричневый болет со светлой шляпкой. Промт v12+v13 закрывает часть эффекта, но распределение видов всё ещё слегка смещено в porcini.
</Callout>

<Callout type="info" title="Никакой приватной агрегации">
Показываем только суммарные распределения по районам и неделям — ни ссылок на оригинальные посты, ни авторов. Отдельные фото можно открыть через VK по идентификатору поста; это публичная ссылка автора, ничего дополнительного мы не публикуем.
</Callout>

## Повторяемость

Код — [github.com/yungkocherov/mushroom_map](https://github.com/yungkocherov/mushroom_map): `pipelines/ingest_vk.py` и `pipelines/prompts/vk_classify_v13.txt`. Модель — `qwen/qwen3.5-9b` в LM Studio с `chat_template_kwargs.enable_thinking=False`. Агрегирующие SQL — в `services/api/src/api/routes/stats.py`.
